import { useState, useMemo, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin, Search, ChevronRight, ChevronLeft, Star, Check,
  Stethoscope, Video, UserCheck, Calculator, Download, TrendingDown,
  X, ChevronDown, Shield, Zap, Award, Clock, Building2, Trash2, Calendar, Sparkles,
  LayoutGrid, Table, Filter, Columns, CheckSquare, Square, Plus
} from "lucide-react";
import MemberLayout from "@/layouts/MemberLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  useListMasterCptCodes, useListProviders, useListCostEstimates,
  useCreateCostEstimate, useDeleteCostEstimate, getListCostEstimatesQueryKey,
  useGetStates, useGetLocalities, useGetCodeTypes, useGetProviderById,
  useGetProviderTypes,
} from "@/lib/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

const COLORS = ["#2563EB", "#22C55E"];
const INS_RATE = 0.7;

const DEFAULT_US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA",
  "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT",
  "VA", "WA", "WV", "WI", "WY", "DC",
];

const POPULAR_LOCATIONS = ["New York, NY", "Los Angeles, CA", "Chicago, IL", "Houston, TX", "Phoenix, AZ"];
const STEP_LABELS = ["Billing Code Search", "State & Locality", "Price Compare Chart"];

// Gradient palette for provider avatars
const AVATAR_GRADIENTS = [
  "from-blue-500 to-indigo-600",
  "from-violet-500 to-purple-600",
  "from-cyan-500 to-blue-500",
  "from-emerald-500 to-teal-600",
  "from-rose-500 to-pink-600",
  "from-amber-500 to-orange-500",
];

// Category badge colors
const CATEGORY_COLORS: Record<string, string> = {
  "Evaluation & Management": "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
  "Radiology": "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
  "Cardiology": "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  "Laboratory": "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  "Physical Therapy": "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  "Mental Health": "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
  "Surgery": "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
};

type Step = 0 | 1 | 2;

interface SelectedService {
  id: number;
  code: string;
  description: string;
  price: number;
  category: string;
}

function MultiSelect({
  options = [], value = [], onChange, placeholder, localityMap = {},
}: {
  options: any[]; value: string[]; onChange: (v: string[]) => void;
  placeholder: string; localityMap?: Record<string, string>;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const normalizedOptions = useMemo(() => {
    return (options || []).map((o: any) => {
      if (typeof o === "string") return { value: o, label: o };
      if (typeof o === "object" && o !== null) {
        const val = String(o.value ?? o.id ?? o.localityId ?? o.code ?? o.name ?? "");
        const rawLbl = o.label ?? o.localityName ?? o.name ?? o.locality ?? o.description ?? o.value ?? o.id ?? "";
        const lbl = typeof rawLbl === "object" && rawLbl !== null
          ? String(rawLbl.name || rawLbl.localityName || rawLbl.code || rawLbl.id || "Option")
          : String(rawLbl || val || "Option");
        return { value: val, label: o.label ? String(o.label) : `${lbl}${o.state ? ` (${o.state})` : ""}` };
      }
      return { value: String(o ?? ""), label: String(o ?? "") };
    });
  }, [options]);

  const filtered = useMemo(() => {
    return normalizedOptions.filter(o =>
      String(o.label).toLowerCase().includes((search || "").toLowerCase())
    );
  }, [normalizedOptions, search]);

  const safeValue = Array.isArray(value) ? value : [];

  const toggle = (optValue: string) => {
    const strVal = String(optValue);
    onChange(safeValue.includes(strVal) ? safeValue.filter(v => v !== strVal) : [...safeValue, strVal]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full min-h-[48px] px-4 py-2 border border-slate-200 dark:border-slate-700/80 rounded-2xl bg-white dark:bg-slate-900 text-sm text-left flex flex-wrap gap-1.5 items-center hover:border-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all shadow-sm"
        >
          {safeValue.length === 0 ? (
            <span className="text-slate-400 font-normal">{placeholder}</span>
          ) : (
            <div className="flex flex-wrap gap-1.5 items-center flex-1 pr-2">
              {safeValue.slice(0, 3).map((v, idx) => {
                const foundOpt = normalizedOptions.find(o => o.value === v);
                let label = foundOpt?.label || localityMap?.[v];
                if (!label || label === v || v.length > 20) {
                  label = localityMap?.[v] || (foundOpt?.label && foundOpt.label !== v ? foundOpt.label : "Selected Area");
                }
                const itemKey = typeof v === 'string' ? v : `val-${idx}`;
                return (
                  <Badge key={itemKey} variant="secondary" className="text-xs rounded-lg px-2.5 py-1 bg-primary/10 text-primary border border-primary/20 font-medium">
                    {String(label)}
                    <span
                      className="ml-1.5 cursor-pointer hover:text-destructive text-primary/70 font-bold"
                      onClick={e => { e.stopPropagation(); toggle(v); }}
                    >&times;</span>
                  </Badge>
                );
              })}
              {safeValue.length > 3 && (
                <Badge variant="outline" className="text-xs rounded-lg px-2.5 py-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 font-semibold">
                  +{safeValue.length - 3} more
                </Badge>
              )}
            </div>
          )}
          <ChevronDown className="w-4 h-4 ml-auto flex-shrink-0 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl overflow-hidden" align="start">
        <div className="p-2.5 border-b border-slate-100 dark:border-slate-800 space-y-2">
          <Input
            placeholder="Search localities..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="h-9 text-xs rounded-xl border-slate-200 dark:border-slate-700"
          />
          <div className="flex items-center justify-between text-[11px] px-1 text-slate-500">
            <span>{safeValue.length} selected</span>
            <div className="flex gap-2">
              <button type="button" onClick={() => onChange(filtered.map(o => o.value))} className="text-primary font-bold hover:underline">Select All</button>
              <button type="button" onClick={() => onChange([])} className="text-slate-400 hover:underline">Clear</button>
            </div>
          </div>
        </div>
        <div className="max-h-56 overflow-y-auto p-1 divide-y divide-slate-50 dark:divide-slate-800/50">
          {filtered.length === 0 ? (
            <div className="p-3 text-center text-xs text-slate-400">No matching localities</div>
          ) : (
            filtered.map(o => {
              const isSelected = safeValue.includes(o.value);
              return (
                <div
                  key={o.value}
                  onClick={() => toggle(o.value)}
                  className={`flex items-center justify-between px-3 py-2 text-xs rounded-xl cursor-pointer transition-colors ${
                    isSelected ? "bg-primary/10 text-primary font-bold" : "hover:bg-slate-50 dark:hover:bg-slate-800/60 text-slate-700 dark:text-slate-300"
                  }`}
                >
                  <span className="truncate pr-2">{o.label}</span>
                  {isSelected && <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" />}
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`w-3 h-3 ${i < Math.round(rating) ? "fill-amber-400 text-amber-400" : "text-slate-200 dark:text-slate-700"}`}
        />
      ))}
    </div>
  );
}

export default function CostEstimates() {
  const [step, setStep] = useState<Step>(0);
  const [selectedState, setSelectedState] = useState("");
  const [selectedLocalityIds, setSelectedLocalityIds] = useState<string[]>([]);
  const [location, setLocation] = useState("");
  
  const [services, setServices] = useState<SelectedService[]>([]);
  const [cptOpen, setCptOpen] = useState(false);
  const [cptSearch, setCptSearch] = useState("");
  const [selectedCodeType, setSelectedCodeType] = useState("ALL");

  const [providerSearch, setProviderSearch] = useState("");
  const [selectedSpecialty, setSelectedSpecialty] = useState("ALL");
  const [viewMode, setViewMode] = useState<"cards" | "compare">("cards");
  const [selectedCompareProviderIds, setSelectedCompareProviderIds] = useState<number[]>([]);
  const [selectedCompareCodeIds, setSelectedCompareCodeIds] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [savedProviders, setSavedProviders] = useState<Set<number>>(new Set());
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [selectedLinkedProviders, setSelectedLinkedProviders] = useState<Record<number, any>>({});
  const [targetProviderNotice, setTargetProviderNotice] = useState<string | null>(null);
  const [isCareTeamGfe, setIsCareTeamGfe] = useState(false);
  const [targetProviderName, setTargetProviderName] = useState("");
  const [careTeamProviderId, setCareTeamProviderId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const pId = searchParams.get("providerId");
      const pName = searchParams.get("providerName");
      const pLoc = searchParams.get("location");
      const pState = searchParams.get("state");

      if (pId) {
        setCareTeamProviderId(pId);
      }
      if (pName) {
        setProviderSearch(pName);
        setTargetProviderName(pName);
        setIsCareTeamGfe(true);
      }
      
      let cleanLoc = (pLoc && pLoc !== "Address not available") ? pLoc : "";
      if (!cleanLoc && pName) {
        cleanLoc = `${pName}'s Practice Clinic`;
      }

      if (cleanLoc) {
        setLocation(cleanLoc);
      }
      if (pState) {
        setSelectedState(pState);
      }

      if (pName || cleanLoc) {
        setTargetProviderNotice(
          `Generating Good Faith Estimate (GFE) for ${pName ? `${pName}` : "Provider"}${cleanLoc ? ` at ${cleanLoc}` : ""}`
        );
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  const { data: statesData } = useGetStates();
  const US_STATES = useMemo(() => {
    if (!statesData || !Array.isArray(statesData) || statesData.length === 0) return DEFAULT_US_STATES;
    return statesData.map((s: any) => typeof s === 'string' ? s : String(s?.code || s?.name || s?.id || '')).filter(Boolean);
  }, [statesData]);

  const localityCacheRef = useRef<Record<string, string>>({});
  const { data: localitiesData } = useGetLocalities(selectedState);
  useEffect(() => {
    if (Array.isArray(localitiesData)) {
      localitiesData.forEach((loc: any) => {
        if (typeof loc === 'object' && loc !== null) {
          const id = String(loc.id ?? loc.localityId ?? loc.value ?? "");
          const name = String(loc.name ?? loc.localityName ?? loc.label ?? loc.description ?? id);
          const stateStr = loc.state ? ` (${loc.state})` : "";
          if (id) localityCacheRef.current[id] = `${name}${stateStr}`;
        }
      });
    }
  }, [localitiesData]);

  const LOCALITIES = useMemo(() => {
    if (!localitiesData || !Array.isArray(localitiesData)) return [];
    return localitiesData.map((loc: any) => {
      if (typeof loc === 'string') return { value: loc, label: loc };
      if (typeof loc === 'object' && loc !== null) {
        const val = String(loc.id ?? loc.localityId ?? loc.code ?? loc.value ?? "");
        const labelStr = String(loc.name ?? loc.localityName ?? loc.label ?? loc.description ?? val);
        const stateStr = loc.state ? ` (${loc.state})` : "";
        return { value: val, label: `${labelStr}${stateStr}` };
      }
      return { value: String(loc), label: String(loc) };
    });
  }, [localitiesData]);

  const { data: codeTypesData } = useGetCodeTypes();
  const codeTypes = useMemo(() => {
    if (!codeTypesData || !Array.isArray(codeTypesData)) return [];
    return codeTypesData.map((ct: any) => typeof ct === 'string' ? ct : String(ct?.name || ct?.code || '')).filter(Boolean);
  }, [codeTypesData]);

  const { data: providerTypesData } = useGetProviderTypes();
  const providerSpecialties = useMemo(() => {
    const defaults = [
      "Anesthesiology", "Cardiology", "Dermatology", "Evaluation & Management",
      "Family Medicine", "General Practice", "Internal Medicine", "Neurology",
      "Orthopedics", "Pediatrics", "Physical Therapy", "Radiology", "Surgery"
    ];
    if (!providerTypesData || !Array.isArray(providerTypesData)) return defaults;
    const fetched = providerTypesData.map((pt: any) => typeof pt === 'string' ? pt : String(pt?.name || pt?.title || '')).filter(Boolean);
    return Array.from(new Set([...defaults, ...fetched])).sort();
  }, [providerTypesData]);

  const { data: cptCodesData } = useListMasterCptCodes({ limit: 1000 });
  const cptCodes = cptCodesData?.items || [];
  
  const effectiveLocationParam = selectedState || location;

  // When GFE is from Care Team with a specific provider, fetch that provider directly
  const { data: careTeamProviderData, isLoading: careTeamProviderLoading } = useGetProviderById(
    isCareTeamGfe && careTeamProviderId && step === 2 ? careTeamProviderId : null
  );

  const { data: providersData, isLoading: providersSearchLoading } = useListProviders(
    step === 2 && !isCareTeamGfe ? {
      state: selectedState || undefined,
      localityIds: selectedLocalityIds.length > 0 ? selectedLocalityIds.join(",") : undefined,
      location: location || undefined,
      cptCode: services.map(s => s.code).join(","),
      specialty: selectedSpecialty !== "ALL" ? selectedSpecialty : undefined,
      page: currentPage,
      limit: itemsPerPage,
      search: providerSearch || undefined
    } : undefined,
    { query: { enabled: step === 2 && !isCareTeamGfe, queryKey: ["providers", selectedState, selectedLocalityIds, location, services.map(s => s.code).join(","), selectedSpecialty, step, currentPage, itemsPerPage, providerSearch] } }
  );

  // Normalize care team provider data into the same shape as search results
  const normalizedCareTeamProvider = useMemo(() => {
    if (!careTeamProviderData) return null;
    const p = careTeamProviderData;
    const prov = p.provider || p;
    const displayName = p.displayName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.name || 'Provider';
    const providerTypes: any[] = prov.providerTypes || [];
    const specialty = providerTypes.map((pt: any) => typeof pt === 'string' ? pt : (pt.name || pt.code || '')).filter(Boolean).join(', ') || prov.specialty || 'Healthcare Provider';
    const allReviews = [...(prov.reviews || []), ...(prov.groupReviews || [])];
    const avgRating = allReviews.length > 0 ? (allReviews.reduce((s: number, r: any) => s + (r.rating || 0), 0) / allReviews.length) : 0;
    const primaryAddress = (p.addresses || [])[0] || {};
    const addressStr = primaryAddress.formatted || [primaryAddress.street1, primaryAddress.city, primaryAddress.state, primaryAddress.zipCode].filter(Boolean).join(', ') || location || 'Address on file';

    return {
      id: p.id || prov.id,
      name: displayName,
      specialty,
      visitType: prov.visitType || 'In-Person',
      gender: prov.gender || '',
      rating: parseFloat(avgRating.toFixed(1)),
      reviewCount: allReviews.length,
      location: addressStr,
      state: primaryAddress.state || selectedState || '',
      city: primaryAddress.city || '',
      zipCode: primaryAddress.zipCode || '',
      distance: null,
      price: null,
      savings: null,
      providerType: specialty,
      credentials: (prov.providerCredentials || []).map((c: any) => c.credentialType?.name || c.name || '').filter(Boolean).join(', '),
      linkedProviders: [],
    };
  }, [careTeamProviderData, location, selectedState]);

  const providersLoading = isCareTeamGfe ? careTeamProviderLoading : providersSearchLoading;
  const rawProviders = isCareTeamGfe
    ? (normalizedCareTeamProvider ? [normalizedCareTeamProvider] : [])
    : (providersData?.items || []);

  const filteredProviders = useMemo(() => {
    let list = rawProviders;
    if (selectedSpecialty !== "ALL") {
      list = list.filter((prov: any) => {
        const provSpec = (typeof prov.specialty === 'object' && prov.specialty !== null
          ? (prov.specialty.name || prov.specialty.title || "")
          : String(prov.specialty || prov.providerType || prov.headline || "")).toLowerCase();
        return provSpec.includes(selectedSpecialty.toLowerCase());
      });
    }
    return list;
  }, [rawProviders, selectedSpecialty]);

  const providers = filteredProviders;

  const displayedCompareProviders = useMemo(() => {
    if (selectedCompareProviderIds.length > 0) {
      const selected = filteredProviders.filter((p: any) => selectedCompareProviderIds.includes(p.id));
      if (selected.length > 0) return selected;
    }
    return filteredProviders;
  }, [filteredProviders, selectedCompareProviderIds]);

  const activeCompareServices = useMemo(() => {
    if (selectedCompareCodeIds.length > 0) {
      const selected = services.filter(s => selectedCompareCodeIds.includes(s.id));
      if (selected.length > 0) return selected;
    }
    return services;
  }, [services, selectedCompareCodeIds]);

  const toggleCompareProvider = (id: number) => {
    setSelectedCompareProviderIds(prev =>
      prev.includes(id) ? prev.filter(pId => pId !== id) : [...prev, id]
    );
  };

  const clearCompareProviders = () => {
    setSelectedCompareProviderIds([]);
  };

  const selectAllCompareProviders = () => {
    setSelectedCompareProviderIds(filteredProviders.map((p: any) => p.id));
  };

  const totalEntries = isCareTeamGfe ? (normalizedCareTeamProvider ? 1 : 0) : (providersData?.totalCount || filteredProviders.length || 0);
  const totalPages = isCareTeamGfe ? 1 : (providersData?.totalPages || 1);
  const { data: estimates, isLoading: estimatesLoading } = useListCostEstimates({});
  const createEstimate = useCreateCostEstimate();
  const deleteEstimate = useDeleteCostEstimate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const activeCodes = useMemo(() =>
    (cptCodes ?? []).filter((c: any) => {
      const matchesSearch = !cptSearch ||
        c.code.toLowerCase().includes(cptSearch.toLowerCase()) ||
        c.description.toLowerCase().includes(cptSearch.toLowerCase());
      
      if (!matchesSearch) return false;
      if (selectedCodeType === "ALL") return true;

      const cType = typeof c.codeType === 'object' && c.codeType !== null
        ? ((c.codeType as any)?.name || (c.codeType as any)?.code || "")
        : String(c.codeType || "");
      return cType.toLowerCase() === selectedCodeType.toLowerCase();
    }), [cptCodes, cptSearch, selectedCodeType]);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setCptOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleSaveEstimate = (
    providerId: number, 
    providerName: string,
    providerType: string,
    estimatedCost: number,
    insuranceCoverage: number,
    patientResponsibility: number
  ) => {
    if (services.length === 0) return;
    setSavingId(providerId);
    createEstimate.mutate(
      { 
        data: { 
          procedure: services.map(s => s.description).join(" + "),
          cptCodes: services.map(s => s.code).join(", "),
          providerId, 
          providerName,
          providerType,
          estimatedCost,
          insuranceCoverage,
          patientResponsibility,
          insurancePlan: "StraightDoc" 
        } 
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListCostEstimatesQueryKey() });
          setSavedProviders(s => new Set([...Array.from(s), providerId]));
          toast({ title: "Estimate saved!", description: `Saved estimate with ${providerName} for ${services.map(s => s.code).join(", ")}.` });
          setSavingId(null);
        },
        onError: () => setSavingId(null),
      }
    );
  };

  const handleDeleteEstimate = (id: number) => {
    setDeletingId(id);
    deleteEstimate.mutate(
      { id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListCostEstimatesQueryKey() });
          toast({ title: "Estimate removed", description: "The estimate has been deleted." });
          setDeletingId(null);
        },
        onError: () => setDeletingId(null),
      }
    );
  };

  const resetFlow = () => {
    setStep(0); setSelectedState(""); setSelectedLocalityIds([]); setLocation(""); setServices([]); setCptSearch(""); setSavedProviders(new Set());
    setProviderSearch(""); setCurrentPage(1); setSelectedCodeType("ALL");
  };

  const totalCost = useMemo(() => {
    if (services.length === 0) return 150;
    const sum = services.reduce((acc, s) => {
      const rawP = Number(s.price);
      const p = isNaN(rawP) || rawP <= 0 ? 150 : rawP;
      return acc + p;
    }, 0);
    return isNaN(sum) || sum <= 0 ? 150 : sum;
  }, [services]);

  const patientCost = Math.round(totalCost * (1 - INS_RATE));
  const insuranceCost = Math.round(totalCost * INS_RATE);

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, '...', totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    return pages;
  };

  return (
    <MemberLayout>
      <div className="w-full space-y-6 sm:space-y-7 pt-4 sm:pt-6 lg:pt-8 pb-16 flex flex-col min-h-[calc(100vh-10rem)]">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-3.5 sm:gap-4">
            <div className="p-3 sm:p-3.5 bg-primary/10 rounded-2xl flex-shrink-0 text-primary">
              <Calculator className="w-6 h-6 sm:w-7 sm:h-7" />
            </div>
            <div className="space-y-0.5">
              <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                Good Faith Estimates (GFE)
              </h1>
              <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm font-medium">
                Find providers and know your costs before your visit
              </p>
            </div>
          </div>
        </div>

        {targetProviderNotice && (
          <div className="p-4 sm:p-5 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-900 dark:text-emerald-200 flex items-center justify-between gap-3 text-xs font-semibold shadow-xs">
            <div className="flex items-center gap-2.5 sm:gap-3">
              <div className="p-2 rounded-xl bg-emerald-600 text-white font-bold flex-shrink-0">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <p className="font-extrabold text-sm sm:text-base text-foreground">{targetProviderNotice}</p>
                <p className="text-[11px] sm:text-xs text-muted-foreground font-normal">Select billing codes below to calculate estimated charges for this provider.</p>
              </div>
            </div>
            <button type="button" onClick={() => setTargetProviderNotice(null)} className="text-muted-foreground hover:text-foreground text-sm font-bold px-2 py-1 cursor-pointer">
              &times;
            </button>
          </div>
        )}

        {/* Wizard Card */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800 relative">

          {/* Step tabs */}
          <div className="grid grid-cols-3 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 rounded-t-2xl sm:rounded-t-3xl overflow-hidden">
            {STEP_LABELS.map((label, i) => {
              const done = step > i;
              const active = step === i;
              return (
                <button
                  key={i}
                  onClick={() => done ? setStep(i as Step) : undefined}
                  className={`relative flex items-center justify-center gap-1.5 sm:gap-2.5 py-3.5 sm:py-4 px-2 sm:px-4 text-xs sm:text-sm font-bold transition-all ${
                    active ? "text-primary" : done ? "text-slate-600 dark:text-slate-300 hover:text-primary cursor-pointer" : "text-slate-400 dark:text-slate-600 cursor-default"
                  }`}
                >
                  {active && (
                    <motion.div layoutId="tab-indicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
                  )}
                  <span className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-colors ${
                    active ? "bg-primary text-white shadow-md shadow-primary/30" :
                    done ? "bg-emerald-500 text-white" :
                    "bg-slate-100 dark:bg-slate-800 text-slate-400"
                  }`}>
                    {done ? <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : i + 1}
                  </span>
                  <span className="hidden sm:inline font-semibold">{label}</span>
                  {done && i < 2 && <ChevronRight className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600 hidden md:block" />}
                </button>
              );
            })}
          </div>

          <AnimatePresence mode="wait">

            {/* ── STEP 1: Billing Code Search ── */}
            {step === 0 && (
              <motion.div key="s0" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.18 }} className="p-4 sm:p-6 lg:p-8 space-y-5 sm:space-y-6 min-h-[380px] sm:min-h-[420px] flex flex-col justify-between">
                <div className="space-y-5 sm:space-y-6">
                  <div className="flex items-start gap-3.5 sm:gap-4">
                    <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center flex-shrink-0 text-cyan-600 dark:text-cyan-400">
                      <Stethoscope className="w-5 h-5 sm:w-6 sm:h-6" />
                    </div>
                    <div className="space-y-0.5">
                      <h2 className="font-bold text-slate-900 dark:text-white text-lg sm:text-xl">Search & Select Billing Codes</h2>
                      <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm">Select billing code type and search CPT / procedure codes to compare rates</p>
                    </div>
                  </div>

                  {/* Selected pill shown above input when a service is chosen */}
                  {services.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-3 sm:p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200/80 dark:border-slate-700/80 flex items-center gap-2 flex-wrap"
                    >
                      {services.map(srv => (
                        <span key={srv.id} className="inline-flex items-center gap-2 bg-primary/10 border border-primary/25 rounded-xl px-3 py-1.5 text-xs sm:text-sm max-w-full font-medium shadow-2xs">
                          <span className="font-mono font-bold text-primary text-xs bg-primary/15 px-1.5 py-0.5 rounded-md flex-shrink-0">{srv.code}</span>
                          <span className="text-slate-700 dark:text-slate-300 truncate max-w-[180px] sm:max-w-xs">{srv.description}</span>
                          <button
                            type="button"
                            onClick={() => setServices(prev => prev.filter(s => s.id !== srv.id))}
                            className="flex-shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors ml-1 cursor-pointer"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      ))}
                    </motion.div>
                  )}

                  {/* Code Type Filter & Procedure Search */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5 sm:gap-4" ref={dropdownRef}>
                    {/* Code Type Filter Dropdown */}
                    <div className="space-y-1.5 md:col-span-1">
                      <label className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider block">
                        Code Type Filter
                      </label>
                      <Select value={selectedCodeType} onValueChange={setSelectedCodeType}>
                        <SelectTrigger className="h-11 sm:h-12 text-xs sm:text-sm rounded-xl sm:rounded-2xl border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm font-medium cursor-pointer">
                          <SelectValue placeholder="All Code Types" />
                        </SelectTrigger>
                        <SelectContent className="rounded-2xl shadow-2xl z-50 max-h-60 cursor-pointer">
                          <SelectItem value="ALL" className="cursor-pointer font-semibold text-xs sm:text-sm">All Code Types</SelectItem>
                          {codeTypes.map((ct: any, idx: number) => {
                            const label = typeof ct === "string" ? ct : (ct?.name || ct?.code || "Code Type");
                            const valueStr = String(label);
                            return (
                              <SelectItem key={ct?.id || `${valueStr}-${idx}`} value={valueStr} className="cursor-pointer text-xs sm:text-sm">
                                {valueStr}
                              </SelectItem>
                            );
                          })}
                          {codeTypes.length === 0 && (
                            <>
                              <SelectItem value="CPT" className="cursor-pointer text-xs sm:text-sm">CPT</SelectItem>
                              <SelectItem value="HCPCS" className="cursor-pointer text-xs sm:text-sm">HCPCS</SelectItem>
                              <SelectItem value="ICD-10" className="cursor-pointer text-xs sm:text-sm">ICD-10</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Results dropdown */}
                    <AnimatePresence>
                      {cptOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 4 }}
                          transition={{ duration: 0.12 }}
                          className="absolute left-0 right-0 top-full mt-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl z-50 overflow-hidden"
                        >
                          <div className="max-h-64 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800/60 p-1">
                            {activeCodes.length === 0 ? (
                              <div className="py-8 text-center text-slate-400 text-xs font-medium">
                                No procedures found matching "{cptSearch}"
                              </div>
                            ) : activeCodes.map((c: any) => {
                              const isSelected = services.some(s => s.code === c.code);
                              const rawCat = typeof c.category === 'object' && c.category !== null
                                ? (c.category.name || c.category.code || "")
                                : String(c.category || "");
                              const isInvalidCat = rawCat && ["PFS", "CLFS", "CPT", "HCPCS"].includes(String(rawCat).trim().toUpperCase());
                              const categoryName = (!isInvalidCat && rawCat) ? rawCat : "General";
                              const standardPrice = Number(c.standardPrice || c.price || 150);
                              return (
                                <button
                                  key={c.id}
                                  type="button"
                                  onClick={() => {
                                    if (isSelected) {
                                      setServices(prev => prev.filter(s => s.code !== c.code));
                                    } else {
                                      setServices(prev => [
                                        ...prev,
                                        {
                                          id: c.id,
                                          code: c.code,
                                          description: c.description || c.codeShortDesc || c.code,
                                          price: Number(c.standardPrice || c.price || 150),
                                          category: categoryName || "General"
                                        }
                                      ]);
                                    }
                                    setCptSearch("");
                                    setCptOpen(false);
                                  }}
                                  className={`w-full text-left px-3.5 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors flex items-center justify-between group rounded-xl my-0.5 ${
                                    isSelected ? "bg-primary/5 dark:bg-primary/10 font-semibold" : ""
                                  }`}
                                >
                                  <div className="min-w-0 pr-3 space-y-0.5">
                                    <div className="flex items-center gap-2">
                                      <span className="font-mono text-xs font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                                        {c.code}
                                      </span>
                                      {categoryName && (
                                        <span className="text-[10px] text-slate-400 dark:text-slate-500 font-semibold uppercase tracking-wider">
                                          {categoryName}
                                        </span>
                                      )}
                                      </div>
                                      <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 line-clamp-2 leading-relaxed">{c.description}</p>
                                    </div>
                                    <div className="flex items-center gap-2.5 flex-shrink-0">
                                      <span className="font-mono font-bold text-xs text-slate-500 dark:text-slate-400">
                                        ${standardPrice.toFixed(0)}
                                      </span>
                                      <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
                                        isSelected ? "bg-primary text-white" : "border border-slate-300 dark:border-slate-600 text-transparent group-hover:border-primary group-hover:text-primary"
                                      }`}>
                                        <Check className="w-3 h-3" />
                                      </div>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                <div className="flex justify-end pt-3 sm:pt-4 border-t border-slate-100 dark:border-slate-800">
                  <Button
                    onClick={() => setStep(1)}
                    disabled={services.length === 0}
                    className="w-full sm:w-auto sm:min-w-[260px] h-11 sm:h-12 rounded-xl sm:rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white border-0 font-bold text-sm sm:text-base gap-2 shadow-md shadow-blue-500/25 active:scale-[0.99] transition-all disabled:opacity-50 disabled:shadow-none cursor-pointer"
                    data-testid="button-service-continue"
                  >
                    Continue to State & Locality <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* ── STEP 2: State & Locality Selection ── */}
            {step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.18 }} className="p-4 sm:p-6 lg:p-8 space-y-5 sm:space-y-6">
                <div className="flex items-start gap-3.5 sm:gap-4">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-primary/20 to-cyan-500/20 flex items-center justify-center flex-shrink-0 text-primary">
                    <MapPin className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div className="space-y-0.5">
                    <h2 className="font-bold text-slate-900 dark:text-white text-lg sm:text-xl">Select State & Locality</h2>
                    <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm">We'll search for providers offering your selected billing codes in this locality</p>
                  </div>
                </div>

                {isCareTeamGfe ? (
                  <div className="p-4 sm:p-6 rounded-2xl sm:rounded-3xl bg-gradient-to-r from-emerald-500/10 via-primary/10 to-emerald-500/5 border border-emerald-500/30 space-y-3.5 sm:space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex items-center gap-2 font-bold text-xs sm:text-sm text-foreground">
                        <MapPin className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                        <span>Practice Location Locked to Provider</span>
                      </div>
                      <Badge className="bg-emerald-600 text-white font-bold text-xs px-3 py-1 rounded-full gap-1 shadow-xs w-fit">
                        <Sparkles className="w-3.5 h-3.5" /> Care Team Provider
                      </Badge>
                    </div>

                    <div className="p-3.5 sm:p-4 bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl border border-border space-y-2 shadow-xs">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="font-extrabold text-sm sm:text-base text-foreground">{targetProviderName || "Care Team Provider"}</p>
                        {selectedState && (
                          <Badge variant="outline" className="text-xs font-bold border-primary/20 text-primary bg-primary/10">
                            State: {selectedState}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-start gap-2 text-xs text-muted-foreground pt-0.5">
                        <Building2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span className="font-semibold text-foreground text-xs sm:text-sm leading-relaxed">
                          {location || "Primary Practice Location"}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs text-muted-foreground pt-1">
                      <p className="text-[11px] leading-relaxed max-w-lg">
                        The Good Faith Estimate calculation is restricted to the clinic location selected for this provider.
                      </p>

                      <button
                        type="button"
                        onClick={() => {
                          setIsCareTeamGfe(false);
                          setTargetProviderNotice(null);
                          setProviderSearch("");
                        }}
                        className="text-xs font-bold text-primary hover:underline flex-shrink-0 cursor-pointer self-start sm:self-auto"
                      >
                        Unlock for general search
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 sm:gap-4">
                    {/* State Select Dropdown */}
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider block">
                        State <span className="text-primary">*</span>
                      </label>
                      <Select
                        value={selectedState}
                        onValueChange={(val) => {
                          setSelectedState(val);
                          setSelectedLocalityIds([]);
                        }}
                      >
                        <SelectTrigger className="h-11 sm:h-12 text-xs sm:text-sm rounded-xl sm:rounded-2xl border-slate-200 dark:border-slate-700/80 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-primary/20 shadow-sm cursor-pointer">
                          <SelectValue placeholder="Select State (e.g. CA, NY, TX)" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60 z-50 rounded-2xl shadow-2xl">
                          {US_STATES.map((s: string) => (
                            <SelectItem key={s} value={s} className="rounded-xl my-0.5">
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Primary Locality MultiSelect Dropdown */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider block">
                          Primary Locality / Area
                        </label>
                        {selectedState && (
                          <span className="text-[11px] text-primary font-semibold px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20">
                            {selectedState}
                          </span>
                        )}
                      </div>
                      <MultiSelect
                        options={LOCALITIES}
                        value={selectedLocalityIds}
                        onChange={setSelectedLocalityIds}
                        localityMap={localityCacheRef.current}
                        placeholder={
                          selectedState
                            ? `Select localities in ${selectedState}...`
                            : "Select state first to view localities..."
                        }
                      />
                    </div>

                    {/* City/Address/Zip Code (Full Width) */}
                    <div className="space-y-1.5 md:col-span-2">
                      <label className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider block">
                        City, Address or ZIP Code <span className="text-slate-400 font-normal lowercase">(Optional)</span>
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-3.5 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          value={location}
                          onChange={(e) => setLocation(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && (selectedState || selectedLocalityIds.length > 0 || location.trim()) && setStep(2)}
                          placeholder="e.g. Los Angeles, 90210, Main St"
                          className="pl-10 sm:pl-11 pr-10 h-11 sm:h-12 text-xs sm:text-sm rounded-xl sm:rounded-2xl border-slate-200 dark:border-slate-700/80 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-primary/20 shadow-sm"
                          data-testid="input-location"
                        />
                        {location && (
                          <button
                            type="button"
                            onClick={() => setLocation("")}
                            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors p-1 cursor-pointer"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="p-3.5 sm:p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800 space-y-2.5">
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-bold">Popular cities</p>
                  <div className="flex flex-wrap gap-2">
                    {POPULAR_LOCATIONS.map(loc => (
                      <button
                        key={loc}
                        type="button"
                        onClick={() => {
                          setLocation(loc);
                          const stateMatch = loc.split(", ")[1];
                          if (stateMatch) setSelectedState(stateMatch);
                        }}
                        className={`text-xs sm:text-sm px-3.5 py-1.5 sm:px-4 sm:py-2 rounded-full border font-medium transition-all cursor-pointer ${
                          location === loc || (selectedState && loc.endsWith(selectedState))
                            ? "bg-primary text-white border-primary shadow-sm shadow-primary/20"
                            : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-primary/50 hover:text-primary hover:bg-primary/5"
                        }`}
                      >
                        {loc}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-3 sm:pt-4 border-t border-slate-100 dark:border-slate-800">
                  <Button variant="outline" onClick={() => setStep(0)} className="w-full sm:w-auto rounded-xl sm:rounded-2xl gap-1.5 border-slate-200 dark:border-slate-700 h-11 sm:h-12 px-6 font-semibold cursor-pointer">
                    <ChevronLeft className="w-4 h-4" /> Back to Codes
                  </Button>
                  <Button
                    onClick={() => setStep(2)}
                    disabled={!selectedState && selectedLocalityIds.length === 0 && !location.trim()}
                    className="w-full sm:w-auto sm:min-w-[260px] h-11 sm:h-12 rounded-xl sm:rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white border-0 font-bold text-sm sm:text-base gap-2 shadow-md shadow-blue-500/25 active:scale-[0.99] transition-all disabled:opacity-50 disabled:shadow-none cursor-pointer"
                    data-testid="button-location-continue"
                  >
                    Compare Provider Rates <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* ── STEP 3: Providers ── */}
            {step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.18 }} className="min-h-[500px] flex flex-col">
                <div className="flex flex-col lg:grid lg:grid-cols-[310px_1fr] lg:divide-x lg:divide-slate-100 dark:lg:divide-slate-800 flex-1">

                  {/* ── LEFT: sticky cost summary ── */}
                  <div className="p-4 sm:p-5 lg:p-6 border-b border-slate-100 dark:border-slate-800 lg:border-b-0 lg:sticky lg:top-4 self-start w-full">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">

                      {/* Search Location details */}
                      <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 space-y-3 flex flex-col justify-between min-w-0">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Your Search</p>
                            <button onClick={() => setStep(0)} className="text-[11px] text-primary font-semibold hover:underline cursor-pointer">Edit</button>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 font-medium">
                            <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            <span className="truncate">{selectedState ? `State: ${selectedState}` : location || "All locations"}</span>
                          </div>
                          <div className="space-y-1.5 pt-1">
                            {services.map(s => (
                              <div key={s.id} className="flex items-start gap-2 text-xs">
                                <Stethoscope className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                  <span className="font-mono font-bold text-primary mr-1">{s.code}</span>
                                  <span className="text-slate-600 dark:text-slate-400 truncate block">{s.description}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Estimated Cost Breakdown */}
                      <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-br from-primary/5 via-blue-50/40 to-indigo-50/20 dark:from-primary/10 dark:via-slate-800/60 dark:to-indigo-950/20 border border-primary/20 space-y-4 shadow-sm min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <p className="text-xs font-bold text-primary uppercase tracking-wider">Estimated Total Cost</p>
                            <p className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white mt-1 tracking-tight">
                              ${totalCost.toLocaleString()}
                            </p>
                          </div>
                          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                            <Calculator className="w-5 h-5" />
                          </div>
                        </div>

                        <div className="w-[140px] h-[140px] relative mx-auto flex items-center justify-center my-1 select-none focus:outline-none outline-none [&_*]:outline-none [&_*:focus]:outline-none [&_.recharts-surface]:outline-none [&_.recharts-pie-sector]:outline-none [&_path]:outline-none [&_path:focus]:outline-none">
                          <PieChart width={140} height={140} margin={{ top: 0, right: 0, bottom: 0, left: 0 }} style={{ outline: "none" }} className="outline-none focus:outline-none select-none">
                            <Pie
                              data={[
                                { name: "Insurance Pays", value: insuranceCost },
                                { name: "You Pay", value: patientCost }
                              ]}
                              cx={70}
                              cy={70}
                              innerRadius={38}
                              outerRadius={54}
                              paddingAngle={3}
                              dataKey="value"
                              style={{ outline: "none" }}
                              className="outline-none focus:outline-none cursor-pointer"
                              isAnimationActive={false}
                            >
                              {COLORS.map((color, idx) => (
                                <Cell key={idx} fill={color} style={{ outline: "none" }} className="outline-none focus:outline-none" />
                              ))}
                            </Pie>
                            <Tooltip
                              wrapperStyle={{ zIndex: 50, outline: "none", pointerEvents: "none" }}
                              content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                  const data = payload[0];
                                  const isInsurance = data.name === "Insurance Pays";
                                  const percent = totalCost > 0 ? `${Math.round((Number(data.value || 0) / totalCost) * 100)}%` : (isInsurance ? "70%" : "30%");
                                  const val = Number(data.value || 0).toLocaleString();
                                  return (
                                    <div className="bg-slate-900/95 dark:bg-slate-800/95 text-white backdrop-blur-md px-3 py-1.5 rounded-xl shadow-xl border border-slate-700/80 text-center pointer-events-none whitespace-nowrap">
                                      <p className="text-[10px] font-bold text-slate-300 uppercase tracking-wider">{data.name}</p>
                                      <p className="text-xs font-black text-white mt-0.5">
                                        ${val}{" "}
                                        <span className={isInsurance ? "text-blue-400 font-bold" : "text-emerald-400 font-bold"}>
                                          ({percent})
                                        </span>
                                      </p>
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                          </PieChart>
                          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none select-none">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Est. Split</span>
                            <span className="text-xs font-extrabold text-slate-700 dark:text-slate-300">70 / 30</span>
                          </div>
                        </div>

                        <div className="space-y-2 pt-1">
                          <div className="flex items-center justify-between text-xs p-2.5 rounded-xl bg-white/80 dark:bg-slate-800/80 border border-slate-200/60 dark:border-slate-700/60 shadow-2xs">
                            <span className="flex items-center gap-2 text-slate-700 dark:text-slate-300 font-medium">
                              <span className="w-2.5 h-2.5 rounded-full bg-blue-600 inline-block flex-shrink-0" />
                              Insurance Pays (~70%)
                            </span>
                            <span className="font-mono font-bold text-slate-900 dark:text-white">${insuranceCost.toLocaleString()}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs p-2.5 rounded-xl bg-white/80 dark:bg-slate-800/80 border border-emerald-200/60 dark:border-emerald-800/40 shadow-2xs">
                            <span className="flex items-center gap-2 text-slate-700 dark:text-slate-300 font-medium">
                              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block flex-shrink-0" />
                              Est. You Pay (~30%)
                            </span>
                            <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">${patientCost.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>

                      <Button variant="outline" onClick={resetFlow} className="sm:col-span-2 lg:col-span-1 w-full rounded-xl sm:rounded-2xl h-10 sm:h-11 text-xs font-semibold border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
                        Start New Search
                      </Button>
                    </div>
                  </div>

                  {/* ── RIGHT: Provider cards & Side-by-Side Compare Chart ── */}
                  <div className="p-4 sm:p-5 lg:p-6 space-y-4 sm:space-y-5 flex flex-col relative min-w-0 flex-1">

                    {/* Results header & Filters */}
                    <div className="flex flex-col gap-3 pb-3 border-b border-slate-100 dark:border-slate-800">
                      {/* Heading & description */}
                      <div className="min-w-0">
                        <h3 className="text-base sm:text-lg font-extrabold text-slate-900 dark:text-white flex items-center gap-2 flex-wrap">
                          <span>{providersLoading ? "Searching providers…" : `${totalEntries} provider${totalEntries !== 1 ? "s" : ""} found`}</span>
                          {selectedSpecialty !== "ALL" && (
                            <Badge variant="secondary" className="bg-primary/10 text-primary border border-primary/20 text-xs font-bold px-2.5 py-0.5 rounded-full">
                              Specialty: {selectedSpecialty}
                            </Badge>
                          )}
                        </h3>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {viewMode === "compare" 
                            ? `Comparing code-level rates side-by-side across ${displayedCompareProviders.length} provider${displayedCompareProviders.length !== 1 ? 's' : ''}` 
                            : "Select providers using checkboxes to compare code-level rates side-by-side"}
                        </p>
                      </div>

                      {/* View Switcher & Compare Action Counter (below heading, buttons centered) */}
                      <div className="flex flex-wrap sm:flex-nowrap items-center justify-center relative w-full pt-0.5">
                        <div className="inline-flex items-center justify-center bg-slate-100 dark:bg-slate-800 p-1.5 rounded-2xl border border-slate-200 dark:border-slate-700 shrink-0 shadow-xs">
                          <button
                            type="button"
                            onClick={() => setViewMode("cards")}
                            className={`flex items-center justify-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all whitespace-nowrap cursor-pointer shrink-0 ${
                              viewMode === "cards"
                                ? "bg-white dark:bg-slate-900 text-primary shadow-xs"
                                : "text-slate-500 hover:text-slate-900 dark:hover:text-white"
                            }`}
                          >
                            <LayoutGrid className="w-4 h-4 sm:w-4.5 sm:h-4.5 shrink-0" />
                            <span>Card View</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setViewMode("compare")}
                            className={`flex items-center justify-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all whitespace-nowrap cursor-pointer shrink-0 ${
                              viewMode === "compare"
                                ? "bg-white dark:bg-slate-900 text-primary shadow-xs"
                                : "text-slate-500 hover:text-slate-900 dark:hover:text-white"
                            }`}
                            data-testid="toggle-compare-view"
                          >
                            <Table className="w-4 h-4 sm:w-4.5 sm:h-4.5 shrink-0" />
                            <span>Side-by-Side Compare</span>
                            {selectedCompareProviderIds.length > 0 && (
                              <span className="bg-primary text-white text-[11px] w-4.5 h-4.5 rounded-full flex items-center justify-center font-bold shrink-0">
                                {selectedCompareProviderIds.length}
                              </span>
                            )}
                          </button>
                        </div>

                        {selectedCompareProviderIds.length > 0 && (
                          <div className="sm:absolute sm:right-0 sm:top-1/2 sm:-translate-y-1/2 mt-1 sm:mt-0">
                            <button
                              type="button"
                              onClick={clearCompareProviders}
                              className="text-xs sm:text-sm font-bold text-slate-400 hover:text-destructive transition-colors px-2.5 py-1.5 rounded-lg hover:bg-destructive/10 cursor-pointer whitespace-nowrap shrink-0"
                            >
                              Clear ({selectedCompareProviderIds.length})
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {!providersLoading && rawProviders.length > 0 && (
                        <div className="flex flex-col gap-2.5 pt-1">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                            {/* Provider Search */}
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                              <Input 
                                value={providerSearch}
                                onChange={e => { setProviderSearch(e.target.value); setCurrentPage(1); }}
                                placeholder="Search provider name..."
                                className="pl-9 h-10 sm:h-11 rounded-xl bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-xs font-medium focus-visible:ring-primary/30"
                              />
                            </div>

                            {/* Specialty Filter Dropdown */}
                            <div className="relative">
                              <Select value={selectedSpecialty} onValueChange={v => { setSelectedSpecialty(v); setCurrentPage(1); }}>
                                <SelectTrigger className="h-10 sm:h-11 text-xs rounded-xl border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 font-semibold text-slate-700 dark:text-slate-300 cursor-pointer">
                                  <div className="flex items-center gap-2 truncate">
                                    <Filter className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                                    <SelectValue placeholder="Filter by Provider Specialty" />
                                  </div>
                                </SelectTrigger>
                                <SelectContent className="max-h-60 z-50 rounded-2xl">
                                  <SelectItem value="ALL" className="text-xs font-bold cursor-pointer">All Provider Specialties</SelectItem>
                                  {providerSpecialties.map((spec: string) => (
                                    <SelectItem key={spec} value={spec} className="text-xs cursor-pointer">
                                      {spec}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          {/* CPT Code level comparison filter chips */}
                          {services.length > 1 && (
                            <div className="flex flex-wrap items-center gap-2 p-2.5 bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-slate-200/60 dark:border-slate-700/60 text-xs">
                              <span className="font-bold text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
                                <Stethoscope className="w-3.5 h-3.5 text-primary" /> Filter Codes to Compare:
                              </span>
                              {services.map(srv => {
                                const isSelected = selectedCompareCodeIds.length === 0 || selectedCompareCodeIds.includes(srv.id);
                                return (
                                  <button
                                    key={srv.id}
                                    type="button"
                                    onClick={() => {
                                      if (selectedCompareCodeIds.length === 0) {
                                        setSelectedCompareCodeIds(services.filter(s => s.id !== srv.id).map(s => s.id));
                                      } else if (isSelected) {
                                        const next = selectedCompareCodeIds.filter(id => id !== srv.id);
                                        setSelectedCompareCodeIds(next.length === 0 ? [] : next);
                                      } else {
                                        setSelectedCompareCodeIds([...selectedCompareCodeIds, srv.id]);
                                      }
                                    }}
                                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer ${
                                      isSelected
                                        ? 'bg-primary/10 text-primary border border-primary/30 font-bold'
                                        : 'bg-white dark:bg-slate-900 text-slate-400 border border-slate-200 dark:border-slate-800 hover:border-slate-300'
                                    }`}
                                  >
                                    <span className="font-mono">{srv.code}</span>
                                    <span className="max-w-[120px] truncate">{srv.description}</span>
                                    {isSelected && <Check className="w-3 h-3 text-primary ml-0.5" />}
                                  </button>
                                );
                              })}
                              {selectedCompareCodeIds.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setSelectedCompareCodeIds([])}
                                  className="text-[11px] text-primary hover:underline font-semibold ml-auto cursor-pointer"
                                >
                                  Show All Codes
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* View Rendering */}
                    {providersLoading ? (
                      <div className="space-y-3 flex-1">
                        {Array.from({ length: 3 }).map((_, i) => (
                          <div key={i} className="rounded-2xl border border-slate-100 dark:border-slate-800 p-5">
                            <div className="flex gap-4">
                              <Skeleton className="w-14 h-14 rounded-2xl flex-shrink-0" />
                              <div className="flex-1 space-y-2">
                                <Skeleton className="h-5 w-44" />
                                <Skeleton className="h-4 w-28" />
                                <Skeleton className="h-4 w-36 mt-1" />
                                <Skeleton className="h-9 w-40 mt-2 rounded-full" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : filteredProviders.length === 0 ? (
                      <div className="py-16 text-center space-y-3 bg-slate-50 dark:bg-slate-800/30 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700">
                        <div className="w-12 h-12 rounded-full bg-amber-500/10 text-amber-500 flex items-center justify-center mx-auto">
                          <Filter className="w-6 h-6" />
                        </div>
                        <h4 className="font-bold text-slate-900 dark:text-white">No providers match the selected specialty filter</h4>
                        <p className="text-xs text-slate-400 max-w-sm mx-auto">
                          Try switching to "All Provider Specialties" or clearing search filters to see all available healthcare providers.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedSpecialty("ALL")}
                          className="rounded-xl text-xs font-bold border-primary/30 text-primary hover:bg-primary/5 cursor-pointer"
                        >
                          Clear Specialty Filter
                        </Button>
                      </div>
                    ) : viewMode === "compare" ? (
                        /* ── SIDE-BY-SIDE COMPARE LIST TABLE VIEW ── */
                        <div className="flex-1 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm flex flex-col overflow-hidden relative">
                          {/* Pinned Header Bar */}
                          <div className="p-3.5 sm:p-4 bg-slate-50 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs shrink-0">
                            <div className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-300 flex-wrap">
                              <div className="w-6 h-6 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                <Table className="w-3.5 h-3.5 text-primary" />
                              </div>
                              <span className="font-bold text-slate-800 dark:text-slate-200">Code-Level Side-by-Side Rate Comparison</span>
                              {selectedCompareProviderIds.length > 0 && (
                                <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 text-[10px] font-bold">
                                  {selectedCompareProviderIds.length} Selected
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={selectAllCompareProviders}
                                className="text-primary font-bold text-xs hover:underline cursor-pointer"
                              >
                                Select All Providers
                              </button>
                              {selectedCompareProviderIds.length > 0 && (
                                <button
                                  type="button"
                                  onClick={clearCompareProviders}
                                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 text-xs hover:underline cursor-pointer"
                                >
                                  Show All ({filteredProviders.length})
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Mobile subtle right-edge scroll hint */}
                          <div className="pointer-events-none absolute right-0 top-14 bottom-0 w-6 bg-gradient-to-l from-white dark:from-slate-900 to-transparent z-30 sm:hidden" />

                          {/* Horizontally Scrollable Table Container */}
                          <div className="w-full overflow-x-auto">
                            <table className="w-full text-left text-xs border-separate border-spacing-0 min-w-[700px]">
                              <thead>
                                <tr className="bg-slate-50 dark:bg-slate-800">
                                  <th className="py-3.5 sm:py-4 px-3 sm:px-4 font-bold text-slate-800 dark:text-slate-200 w-[180px] sm:w-[220px] min-w-[150px] sm:min-w-[190px] sticky left-0 bg-slate-50 dark:bg-slate-800 z-20 border-r border-b border-slate-200 dark:border-slate-800 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.08)]">
                                    Service / CPT Code
                                  </th>
                                  <th className="py-3.5 sm:py-4 px-3 sm:px-4 font-bold text-slate-600 dark:text-slate-400 w-[95px] sm:w-[110px] text-right border-r border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800">
                                    Base Rate
                                  </th>
                                  {displayedCompareProviders.map((prov, i) => {
                                    const gradient = AVATAR_GRADIENTS[i % AVATAR_GRADIENTS.length];
                                    const initials = prov.name.split(" ").map((n: string) => n[0]).slice(0, 2).join("");
                                    const rawMedCharge = typeof prov.medicareCharge === 'object' && prov.medicareCharge !== null
                                      ? ((prov.medicareCharge as any)?.name || (prov.medicareCharge as any)?.rate || "")
                                      : String(prov.medicareCharge || "");
                                    const medicareText = rawMedCharge ? (rawMedCharge.includes('%') ? rawMedCharge : rawMedCharge + '%') : "100%";
                                    const isSelected = selectedCompareProviderIds.includes(prov.id);

                                    return (
                                      <th key={prov.id} className="py-3.5 sm:py-4 px-3.5 sm:px-4 font-bold text-slate-900 dark:text-white min-w-[200px] sm:min-w-[220px] border-r border-b border-slate-200 dark:border-slate-800 align-top bg-slate-50/50 dark:bg-slate-800/50 last:border-r-0">
                                        <div className="flex items-start justify-between gap-2 mb-2">
                                          <div className="flex items-center gap-2.5 min-w-0">
                                            <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-xs ring-2 ring-white dark:ring-slate-800`}>
                                              {initials}
                                            </div>
                                            <div className="min-w-0">
                                              <p className="font-extrabold text-xs text-foreground truncate max-w-[130px]" title={prov.name}>{prov.name}</p>
                                              <Badge variant="outline" className="text-[9px] py-0 px-1.5 bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-400 border-indigo-200 font-bold truncate max-w-[130px]">
                                                {prov.specialty || prov.headline || "Provider"}
                                              </Badge>
                                            </div>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => toggleCompareProvider(prov.id)}
                                            className={`p-1.5 rounded-lg transition-colors cursor-pointer shrink-0 ${isSelected ? 'text-primary bg-primary/10' : 'text-slate-300 hover:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                                            title={isSelected ? "Remove from comparison" : "Select for comparison"}
                                          >
                                            {isSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                                          </button>
                                        </div>

                                        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                                          <Badge variant="outline" className="text-[10px] py-0.5 px-2 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border-emerald-200 font-bold">
                                            {medicareText} Fee Sched
                                          </Badge>
                                          <span className="text-[10px] text-amber-600 dark:text-amber-400 font-bold flex items-center gap-0.5 bg-amber-50 dark:bg-amber-950/30 px-1.5 py-0.5 rounded border border-amber-200/50">
                                            <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                                            {(prov.rating || 0).toFixed(1)}
                                          </span>
                                        </div>
                                      </th>
                                    );
                                  })}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/70">
                                {/* EST. PATIENT RESPONSIBILITY ROW (~30%) */}
                                <tr className="bg-emerald-50/50 dark:bg-emerald-950/20 text-xs">
                                  <td className="py-3.5 px-3 sm:px-4 text-emerald-800 dark:text-emerald-300 font-bold sticky left-0 bg-emerald-50 dark:bg-slate-900 z-10 border-r border-b border-emerald-100 dark:border-slate-800 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.08)]">
                                    Est. You Pay (~30%)
                                  </td>
                                  <td className="py-3.5 px-3 sm:px-4 text-right font-mono font-bold text-slate-600 dark:text-slate-400 border-r border-b border-emerald-100 dark:border-slate-800 bg-emerald-50/40 dark:bg-emerald-950/15">
                                    ${patientCost.toLocaleString()}
                                  </td>
                                  {displayedCompareProviders.map((prov) => {
                                    const isGroupProvider = prov.practiceType?.toLowerCase() === 'group' || prov.practiceType?.toLowerCase() === 'clinic' || prov.isGroup;
                                    const linkedProviders: any[] = prov.linkedProviders || [];
                                    const selectedLinkedProvider = selectedLinkedProviders[prov.id] ?? (linkedProviders[0] || null);
                                    let effectiveMedStr: string;
                                    if (isGroupProvider && selectedLinkedProvider) {
                                      effectiveMedStr = selectedLinkedProvider.medicareCharge ? String(selectedLinkedProvider.medicareCharge).replace('%', '') : '100';
                                    } else {
                                      effectiveMedStr = prov.medicareCharge ? String(prov.medicareCharge).replace('%', '') : '100';
                                    }
                                    const parsedMed = parseFloat(effectiveMedStr);
                                    const medPercent = isNaN(parsedMed) || parsedMed <= 0 ? 1 : parsedMed / 100;
                                    
                                    const provPrice = activeCompareServices.reduce((acc, s) => {
                                      const bp = Number(s.price || 150);
                                      return acc + Math.round(bp * medPercent);
                                    }, 0);
                                    const provPatientCost = Math.round(provPrice * (1 - INS_RATE));

                                    return (
                                      <td key={prov.id} className="py-3.5 px-3.5 sm:px-4 border-r border-b border-emerald-100 dark:border-slate-800 font-mono font-extrabold text-sm text-emerald-700 dark:text-emerald-300 bg-emerald-50/40 dark:bg-emerald-950/15 last:border-r-0">
                                        ${provPatientCost.toLocaleString()}
                                      </td>
                                    );
                                  })}
                                </tr>

                                {/* TOTAL ESTIMATED COST ROW */}
                                <tr className="bg-slate-50 dark:bg-slate-800 font-bold">
                                  <td className="py-3 px-3 sm:px-4 text-slate-900 dark:text-white font-extrabold sticky left-0 bg-slate-50 dark:bg-slate-800 z-10 border-r border-b border-slate-200 dark:border-slate-700 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.08)]">
                                    TOTAL GFE ESTIMATE
                                  </td>
                                  <td className="py-3 px-3 sm:px-4 text-right font-mono font-extrabold text-slate-700 dark:text-slate-300 border-r border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
                                    ${totalCost.toLocaleString()}
                                  </td>
                                  {displayedCompareProviders.map((prov) => {
                                    const isGroupProvider = prov.practiceType?.toLowerCase() === 'group' || prov.practiceType?.toLowerCase() === 'clinic' || prov.isGroup;
                                    const linkedProviders: any[] = prov.linkedProviders || [];
                                    const selectedLinkedProvider = selectedLinkedProviders[prov.id] ?? (linkedProviders[0] || null);
                                    let effectiveMedStr: string;
                                    if (isGroupProvider && selectedLinkedProvider) {
                                      effectiveMedStr = selectedLinkedProvider.medicareCharge ? String(selectedLinkedProvider.medicareCharge).replace('%', '') : '100';
                                    } else {
                                      effectiveMedStr = prov.medicareCharge ? String(prov.medicareCharge).replace('%', '') : '100';
                                    }
                                    const parsedMed = parseFloat(effectiveMedStr);
                                    const medPercent = isNaN(parsedMed) || parsedMed <= 0 ? 1 : parsedMed / 100;
                                    
                                    const provPrice = activeCompareServices.reduce((acc, s) => {
                                      const bp = Number(s.price || 150);
                                      return acc + Math.round(bp * medPercent);
                                    }, 0);

                                    return (
                                      <td key={prov.id} className="py-3 px-3.5 sm:px-4 border-r border-b border-slate-200 dark:border-slate-700 font-mono font-extrabold text-xs text-slate-900 dark:text-white bg-slate-50 dark:bg-slate-800 last:border-r-0">
                                        ${provPrice.toLocaleString()}
                                      </td>
                                    );
                                  })}
                                </tr>

                                {/* ACTION BUTTON ROW */}
                                <tr className="bg-slate-50/70 dark:bg-slate-800/50">
                                  <td className="py-3 px-3 sm:px-4 font-bold text-slate-400 sticky left-0 bg-slate-50 dark:bg-slate-900 z-10 border-r border-b-2 border-slate-200 dark:border-slate-800 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.08)] uppercase text-[10px] tracking-wider">
                                    Action
                                  </td>
                                  <td className="py-3 px-3 sm:px-4 border-r border-b-2 border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40"></td>
                                  {displayedCompareProviders.map((prov) => {
                                    const isSaved = savedProviders.has(prov.id);
                                    const isGroupProvider = prov.practiceType?.toLowerCase() === 'group' || prov.practiceType?.toLowerCase() === 'clinic' || prov.isGroup;
                                    const linkedProviders: any[] = prov.linkedProviders || [];
                                    const selectedLinkedProvider = selectedLinkedProviders[prov.id] ?? (linkedProviders[0] || null);
                                    let effectiveMedStr: string;
                                    if (isGroupProvider && selectedLinkedProvider) {
                                      effectiveMedStr = selectedLinkedProvider.medicareCharge ? String(selectedLinkedProvider.medicareCharge).replace('%', '') : '100';
                                    } else {
                                      effectiveMedStr = prov.medicareCharge ? String(prov.medicareCharge).replace('%', '') : '100';
                                    }
                                    const parsedMed = parseFloat(effectiveMedStr);
                                    const medPercent = isNaN(parsedMed) || parsedMed <= 0 ? 1 : parsedMed / 100;
                                    
                                    const provPrice = activeCompareServices.reduce((acc, s) => {
                                      const bp = Number(s.price || 150);
                                      return acc + Math.round(bp * medPercent);
                                    }, 0);
                                    const provPatientCost = Math.round(provPrice * (1 - INS_RATE));

                                    return (
                                      <td key={prov.id} className="py-3 px-3.5 sm:px-4 border-r border-b-2 border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40 last:border-r-0">
                                        <Button
                                          size="sm"
                                          onClick={() => handleSaveEstimate(
                                            prov.id,
                                            prov.name,
                                            prov.specialty || "Provider",
                                            provPrice,
                                            Math.round(provPrice * INS_RATE),
                                            provPatientCost
                                          )}
                                          disabled={isSaved || savingId === prov.id}
                                          className={`w-full h-8.5 text-xs font-bold gap-1 rounded-xl transition-all shadow-xs cursor-pointer ${
                                            isSaved ? "bg-emerald-600 text-white" : "bg-primary text-white hover:bg-primary/90"
                                          }`}
                                        >
                                          {savingId === prov.id ? "Saving…" : isSaved ? <><Check className="w-3.5 h-3.5" /> Saved</> : <><Check className="w-3.5 h-3.5" /> Save GFE</>}
                                        </Button>
                                      </td>
                                    );
                                  })}
                                </tr>

                                {/* Code-by-code breakdown rows */}
                                {activeCompareServices.map((srv) => {
                                  const baseCodePrice = Number(srv.price || 150);

                                  // Find lowest price for this CPT code across displayed providers
                                  const providerCodeRates = displayedCompareProviders.map((prov) => {
                                    const isGroupProvider = prov.practiceType?.toLowerCase() === 'group' || prov.practiceType?.toLowerCase() === 'clinic' || prov.isGroup;
                                    const linkedProviders: any[] = prov.linkedProviders || [];
                                    const selectedLinkedProvider = selectedLinkedProviders[prov.id] ?? (linkedProviders[0] || null);
                                    let effectiveMedStr: string;
                                    if (isGroupProvider && selectedLinkedProvider) {
                                      effectiveMedStr = selectedLinkedProvider.medicareCharge ? String(selectedLinkedProvider.medicareCharge).replace('%', '') : '100';
                                    } else {
                                      effectiveMedStr = prov.medicareCharge ? String(prov.medicareCharge).replace('%', '') : '100';
                                    }
                                    const parsedMed = parseFloat(effectiveMedStr);
                                    const medPercent = isNaN(parsedMed) || parsedMed <= 0 ? 100 : parsedMed;
                                    return { id: prov.id, price: Math.round(baseCodePrice * (medPercent / 100)) };
                                  });
                                  const lowestCodeRate = Math.min(...providerCodeRates.map(r => r.price));

                                  return (
                                    <tr key={srv.id} className="group hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors">
                                      <td className="py-3 px-3 sm:px-4 font-medium text-slate-800 dark:text-slate-200 sticky left-0 bg-white dark:bg-slate-900 group-hover:bg-slate-50 dark:group-hover:bg-slate-800/80 z-10 border-r border-b border-slate-100 dark:border-slate-800 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.08)]">
                                        <div className="space-y-0.5">
                                          <div className="flex items-center gap-1.5 flex-wrap">
                                            <span className="font-mono font-bold text-xs text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                                              {srv.code}
                                            </span>
                                            {srv.category && (
                                              <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider truncate max-w-[100px] sm:max-w-[120px]">
                                                {srv.category}
                                              </span>
                                            )}
                                          </div>
                                          <span className="text-xs text-slate-700 dark:text-slate-300 font-semibold block truncate max-w-[140px] sm:max-w-[190px]" title={srv.description}>
                                            {srv.description}
                                          </span>
                                        </div>
                                      </td>
                                      <td className="py-3 px-3 sm:px-4 text-right font-mono font-bold text-slate-500 border-r border-b border-slate-100 dark:border-slate-800">
                                        ${baseCodePrice.toFixed(2)}
                                      </td>
                                      {displayedCompareProviders.map((prov) => {
                                        const isGroupProvider = prov.practiceType?.toLowerCase() === 'group' || prov.practiceType?.toLowerCase() === 'clinic' || prov.isGroup;
                                        const linkedProviders: any[] = prov.linkedProviders || [];
                                        const selectedLinkedProvider = selectedLinkedProviders[prov.id] ?? (linkedProviders[0] || null);
                                        let effectiveMedStr: string;
                                        if (isGroupProvider && selectedLinkedProvider) {
                                          effectiveMedStr = selectedLinkedProvider.medicareCharge ? String(selectedLinkedProvider.medicareCharge).replace('%', '') : '100';
                                        } else {
                                          effectiveMedStr = prov.medicareCharge ? String(prov.medicareCharge).replace('%', '') : '100';
                                        }
                                        const parsedMed = parseFloat(effectiveMedStr);
                                        const medPercent = isNaN(parsedMed) || parsedMed <= 0 ? 100 : parsedMed;
                                        const codeProvPrice = Math.round(baseCodePrice * (medPercent / 100));
                                        const diffPct = Math.round(medPercent - 100);
                                        const isLowest = codeProvPrice === lowestCodeRate && displayedCompareProviders.length > 1;

                                        return (
                                          <td key={prov.id} className={`py-3 px-3.5 sm:px-4 border-r border-b border-slate-100 dark:border-slate-800 font-mono font-extrabold text-xs last:border-r-0 ${isLowest ? 'bg-emerald-50/50 dark:bg-emerald-950/20' : ''}`}>
                                            <div className="space-y-1">
                                              <div className="flex items-center justify-between gap-1.5">
                                                <span className="text-slate-900 dark:text-white font-mono text-sm">${codeProvPrice.toFixed(2)}</span>
                                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                                                  diffPct === 0 ? "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400" :
                                                  diffPct < 0 ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400" :
                                                  "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-400"
                                                }`}>
                                                  {diffPct > 0 ? `+${diffPct}%` : diffPct === 0 ? 'Base' : `${diffPct}%`}
                                                </span>
                                              </div>
                                              {isLowest && (
                                                <Badge className="bg-emerald-600 text-white font-bold text-[9px] px-1.5 py-0 rounded-md gap-0.5 shadow-2xs">
                                                  <Award className="w-2.5 h-2.5" /> Lowest Rate
                                                </Badge>
                                              )}
                                            </div>
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : (
                        /* ── CARDS VIEW ── */
                        <div className="space-y-3.5 flex-1 pb-16">
                          {providers.map((prov, i) => {
                          const isSaved = savedProviders.has(prov.id);
                          const gradient = AVATAR_GRADIENTS[i % AVATAR_GRADIENTS.length];
                          const initials = prov.name.split(" ").map((n: string) => n[0]).slice(0, 2).join("");
                          const isBest = i === 0;
                          const isComparing = selectedCompareProviderIds.includes(prov.id);
                          
                          const isGroupProvider = prov.practiceType?.toLowerCase() === 'group' || prov.practiceType?.toLowerCase() === 'clinic' || prov.isGroup;
                          const linkedProviders: any[] = prov.linkedProviders || [];
                          const selectedLinkedProvider = selectedLinkedProviders[prov.id] ?? (linkedProviders[0] || null);

                          let effectiveMedStr: string;
                          if (isGroupProvider && selectedLinkedProvider) {
                            effectiveMedStr = selectedLinkedProvider.medicareCharge
                              ? String(selectedLinkedProvider.medicareCharge).replace('%', '')
                              : '100';
                          } else {
                            effectiveMedStr = prov.medicareCharge ? String(prov.medicareCharge).replace('%', '') : '100';
                          }
                          
                          const parsedMed = parseFloat(effectiveMedStr);
                          const medPercent = isNaN(parsedMed) || parsedMed <= 0 ? 1 : parsedMed / 100;
                          const safeTotal = isNaN(totalCost) || totalCost <= 0 ? 150 : totalCost;
                          const rawProvPrice = Math.round(safeTotal * medPercent);
                          const provPrice = isNaN(rawProvPrice) || rawProvPrice <= 0 ? 150 : rawProvPrice;
                          const provPatientCost = provPrice;

                          const primaryAddress = prov.addresses?.find((a: any) => a.isPrimary) || prov.addresses?.[0];
                          const addressString = primaryAddress 
                            ? `${primaryAddress.street1}${primaryAddress.street2 ? ' ' + primaryAddress.street2 : ''}, ${primaryAddress.city}, ${primaryAddress.state} ${primaryAddress.zipCode}` 
                            : (prov.address || `${selectedState || location}`);

                          return (
                            <motion.div
                              key={prov.id}
                              initial={{ opacity: 0, y: 12 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.06, duration: 0.2 }}
                              className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                                isComparing
                                  ? "border-primary/50 bg-primary/5 dark:bg-primary/10 shadow-md ring-2 ring-primary/20"
                                  : isSaved
                                    ? "border-emerald-200 bg-emerald-50/50 dark:border-emerald-800/40 dark:bg-emerald-900/10"
                                    : isBest
                                      ? "border-primary/25 bg-white dark:bg-slate-900 shadow-md shadow-primary/5"
                                      : "border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-primary/20 hover:shadow-sm"
                              }`}
                            >
                              <div className="flex flex-col">
                                <div className="p-3.5 sm:p-5 flex flex-col xl:flex-row gap-4 sm:gap-5 justify-between">
                                  {/* Main Content (Avatar + Details) */}
                                  <div className="flex-1 flex flex-row items-start gap-3 sm:gap-5 min-w-0 w-full">
                                    {/* Avatar */}
                                    <div className="flex-shrink-0 relative">
                                      <div className={`w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center text-white text-lg sm:text-3xl font-medium shadow-sm ring-2 sm:ring-4 ring-white dark:ring-slate-900`}>
                                        {initials}
                                      </div>
                                      <div className="absolute bottom-0.5 right-0.5 w-3 h-3 sm:w-4 sm:h-4 bg-emerald-500 border-2 border-white dark:border-slate-900 rounded-full"></div>
                                    </div>

                                    {/* Details */}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          {isBest && !isSaved && (
                                            <div className="inline-flex items-center gap-1 text-[#D97706] bg-[#FEF3C7] text-[10px] sm:text-[11px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide shrink-0">
                                              <Check className="w-3 h-3" /> Top Match
                                            </div>
                                          )}
                                        </div>

                                        {/* Compare Selection Checkbox */}
                                        <button
                                          type="button"
                                          onClick={() => toggleCompareProvider(prov.id)}
                                          className={`flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1 rounded-full text-[11px] sm:text-xs font-bold transition-all border cursor-pointer shrink-0 whitespace-nowrap ${
                                            isComparing
                                              ? "bg-primary text-white border-primary shadow-xs"
                                              : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-primary/40"
                                          }`}
                                          data-testid={`compare-checkbox-${prov.id}`}
                                        >
                                          {isComparing ? (
                                            <><CheckSquare className="w-3.5 h-3.5" /> Comparing</>
                                          ) : (
                                            <><Plus className="w-3.5 h-3.5" /> Select to Compare</>
                                          )}
                                        </button>
                                      </div>

                                      <div className="flex items-center justify-between gap-4">
                                        <div className="min-w-0 flex-1">
                                          <h3 className="text-base sm:text-xl font-bold text-slate-900 dark:text-white tracking-tight break-words">{prov.name}</h3>
                                          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-1">
                                            <Badge variant="outline" className="text-[11px] sm:text-xs font-semibold bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-400 border-indigo-200/60 max-w-full">
                                              {prov.specialty || prov.headline || (typeof prov.specialty === 'object' && prov.specialty !== null ? (prov.specialty.name || prov.specialty.title || "") : String(prov.specialty || ""))}
                                            </Badge>
                                            {prov.gender && <span className="text-slate-400 text-xs font-normal"> · {prov.gender}</span>}
                                          </div>
                                        </div>
                                      </div>

                                      {/* Badges */}
                                      {(() => {
                                        const rawMedCharge = typeof prov.medicareCharge === 'object' && prov.medicareCharge !== null
                                          ? ((prov.medicareCharge as any)?.name || (prov.medicareCharge as any)?.rate || "")
                                          : String(prov.medicareCharge || "");
                                        const medicareChargeText = rawMedCharge ? (rawMedCharge.includes('%') ? rawMedCharge : rawMedCharge + '%') : "100% Fee Schedule";
                                        const hospitalName = typeof prov.hospital === 'object' && prov.hospital !== null
                                          ? ((prov.hospital as any)?.name || (prov.hospital as any)?.title || (prov.hospital as any)?.code || "")
                                          : String(prov.hospital || "");

                                        return (
                                          <>
                                            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-2.5 sm:mt-3">
                                              <div className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-md bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-[11px] sm:text-xs font-semibold border border-amber-200/50 dark:border-amber-800/50 shrink-0">
                                                <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500 shrink-0" />
                                                <span>{(prov.rating || 0).toFixed(1)} <span className="text-amber-600/70 dark:text-amber-500/70 font-medium">({prov.reviewCount || 0})</span></span>
                                              </div>
                                              <div className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[11px] sm:text-xs font-medium border border-slate-200 dark:border-slate-700 shrink-0">
                                                <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                                <span>{prov.yearsExperience || 5} yrs exp</span>
                                              </div>
                                              <div className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-md bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 text-[11px] sm:text-xs font-semibold border border-emerald-200/50 dark:border-emerald-800/50 shrink-0">
                                                <Shield className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                                                <span>{medicareChargeText}</span>
                                              </div>
                                            </div>

                                            {/* Location & Time */}
                                            <div className="mt-3 sm:mt-4 space-y-1.5">
                                              <div className="flex items-start gap-2 text-xs sm:text-[13px] text-slate-600 dark:text-slate-400">
                                                <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 shrink-0 mt-0.5" />
                                                <span className="leading-snug break-words min-w-0">
                                                  {hospitalName && <><span className="font-medium text-slate-900 dark:text-slate-200">{hospitalName}</span><br/></>}
                                                  {addressString}
                                                </span>
                                              </div>
                                            </div>
                                          </>
                                        );
                                      })()}

                                      {/* Group: Linked Provider Selector */}
                                      {isGroupProvider && linkedProviders.length > 0 && (
                                        <div className="mt-4 inline-block max-w-full p-3 rounded-2xl bg-indigo-50/40 dark:bg-indigo-900/10 border border-indigo-100/50 dark:border-indigo-800/30">
                                          <p className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                                            <UserCheck className="w-3.5 h-3.5" /> Select Provider
                                          </p>
                                          <div className="flex overflow-x-auto scrollbar-hide gap-1.5 pb-0.5">
                                            {linkedProviders.map((lp: any) => {
                                              const isSelected = (selectedLinkedProvider?.id === lp.id) || (!selectedLinkedProvider && linkedProviders.indexOf(lp) === 0);
                                              const lpMed = lp.medicareCharge ? String(lp.medicareCharge).replace('%','') : '100';
                                              const lpName = typeof lp === 'object' && lp !== null
                                                ? ((lp as any)?.name || (lp.user?.firstName ? `${lp.user.firstName} ${lp.user.lastName}` : "Provider"))
                                                : String(lp || "Provider");
                                              return (
                                                <button
                                                  key={lp.id}
                                                  onClick={() => setSelectedLinkedProviders(prev => ({ ...prev, [prov.id]: lp }))}
                                                  className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-all duration-200 cursor-pointer ${
                                                    isSelected
                                                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm shadow-indigo-600/20'
                                                      : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-indigo-300 hover:text-indigo-600 hover:shadow-sm'
                                                  }`}
                                                >
                                                  <span className="truncate max-w-[140px]">{String(lpName)}</span>
                                                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${isSelected ? 'bg-white/20 text-white' : 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400'}`}>
                                                    {lpMed}%
                                                  </span>
                                                </button>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Right side / Bottom Pricing & CTA */}
                                  <div className="flex flex-col sm:flex-row xl:flex-col items-stretch sm:items-center xl:items-end justify-between xl:justify-center border-t xl:border-t-0 xl:border-l border-slate-100 dark:border-slate-800 pt-3.5 xl:pt-0 xl:pl-5 w-full xl:w-auto xl:min-w-[180px] gap-3">
                                    <div className="flex sm:flex-col items-baseline sm:items-start xl:items-end justify-between sm:justify-start gap-1">
                                      <p className="text-[11px] sm:text-xs text-slate-400 font-semibold uppercase tracking-wider">You pay</p>
                                      <p className="text-2xl sm:text-3xl font-extrabold text-emerald-600 dark:text-emerald-400 tracking-tight">
                                        ${provPatientCost.toLocaleString()}
                                      </p>
                                    </div>

                                    <div className="grid grid-cols-2 sm:flex xl:flex-col gap-2 w-full sm:w-auto xl:w-full">
                                      <Button
                                        onClick={() => handleSaveEstimate(
                                          prov.id,
                                          prov.name,
                                          prov.specialty || "Provider",
                                          provPrice,
                                          Math.round(provPrice * INS_RATE),
                                          provPatientCost
                                        )}
                                        disabled={isSaved || savingId === prov.id}
                                        className={`h-10 px-3 sm:px-4 rounded-xl text-xs font-bold gap-1.5 transition-all shadow-sm cursor-pointer whitespace-nowrap ${
                                          isSaved
                                            ? "bg-emerald-600 text-white border-0"
                                            : "bg-primary text-white hover:bg-primary/90 shadow-primary/20"
                                        }`}
                                      >
                                        {savingId === prov.id ? (
                                          "Saving…"
                                        ) : isSaved ? (
                                          <><Check className="w-4 h-4 shrink-0" /> Save Estimate</>
                                        ) : (
                                          <><Check className="w-4 h-4 shrink-0" /> Save Estimate</>
                                        )}
                                      </Button>

                                      <Button
                                        variant="outline"
                                        onClick={() => {
                                          const slug = prov.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                                          window.open(`/provider/${slug}/${prov.id}`, '_blank');
                                        }}
                                        className="h-10 px-3 sm:px-4 rounded-xl text-xs font-semibold border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer whitespace-nowrap"
                                      >
                                        View Profile
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    )}

                    {/* Floating Sticky Compare Bar for Card View */}
                    {viewMode === "cards" && selectedCompareProviderIds.length > 0 && (
                      <motion.div
                        initial={{ y: 50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 50, opacity: 0 }}
                        className="sticky bottom-4 z-40 mx-auto w-full max-w-xl bg-slate-900/95 dark:bg-slate-800/95 text-white backdrop-blur-md px-3.5 sm:px-4 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl shadow-2xl border border-slate-700/80 flex items-center justify-between gap-3 mt-auto"
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="flex -space-x-2 overflow-hidden flex-shrink-0">
                            {selectedCompareProviderIds.slice(0, 3).map((id, idx) => {
                              const p = providers.find((prov: any) => prov.id === id);
                              if (!p) return null;
                              const initials = p.name.split(" ").map((n: string) => n[0]).slice(0, 2).join("");
                              const gradient = AVATAR_GRADIENTS[idx % AVATAR_GRADIENTS.length];
                              return (
                                <div key={id} className={`w-7 h-7 rounded-full bg-gradient-to-br ${gradient} border-2 border-slate-900 flex items-center justify-center text-[10px] font-bold text-white shadow-xs`} title={p.name}>
                                  {initials}
                                </div>
                              );
                            })}
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-extrabold text-white">
                              {selectedCompareProviderIds.length} provider{selectedCompareProviderIds.length !== 1 ? 's' : ''} selected
                            </p>
                            <p className="text-[10px] text-slate-300 truncate">Compare code-level rates side-by-side</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            type="button"
                            onClick={clearCompareProviders}
                            className="text-xs text-slate-400 hover:text-white px-2 py-1 font-semibold cursor-pointer"
                          >
                            Clear
                          </button>
                          <Button
                            onClick={() => setViewMode("compare")}
                            className="h-8 px-3.5 text-xs font-bold bg-primary hover:bg-primary/90 text-white rounded-xl shadow-lg shadow-primary/30 flex items-center gap-1.5 cursor-pointer"
                            data-testid="button-compare-selected"
                          >
                            <Table className="w-3.5 h-3.5" /> Compare Side-by-Side
                          </Button>
                        </div>
                      </motion.div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-100 dark:border-slate-800 text-xs">
                        <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 text-slate-500">
                          <span>Rows per page:</span>
                          <Select value={String(itemsPerPage)} onValueChange={v => { setItemsPerPage(Number(v)); setCurrentPage(1); }}>
                            <SelectTrigger className="h-7 w-16 text-xs rounded-lg border-slate-200 dark:border-slate-700 cursor-pointer">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="cursor-pointer">
                              <SelectItem value="5" className="cursor-pointer">5</SelectItem>
                              <SelectItem value="10" className="cursor-pointer">10</SelectItem>
                              <SelectItem value="20" className="cursor-pointer">20</SelectItem>
                            </SelectContent>
                          </Select>
                          <span>Showing {(currentPage-1)*itemsPerPage + 1} to {Math.min(currentPage*itemsPerPage, totalEntries)} of {totalEntries} entries</span>
                        </div>

                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="w-7 h-7 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
                          >
                            <ChevronLeft className="w-3.5 h-3.5" />
                          </button>

                          {getPageNumbers().map((p, idx) => typeof p === 'number' ? (
                            <button
                              key={idx}
                              onClick={() => setCurrentPage(p)}
                              className={`w-7 h-7 rounded-lg text-xs font-bold transition-all cursor-pointer ${currentPage === p ? 'bg-primary text-white' : 'hover:bg-slate-100 text-slate-600'}`}
                            >
                              {p}
                            </button>
                          ) : (
                            <span key={idx} className="px-1 text-slate-400">…</span>
                          ))}

                          <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="w-7 h-7 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
                          >
                            <ChevronRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    )}

                  </div>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>
    </MemberLayout>
  );
}
